<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-gray-600 bg-opacity-50 p-4"
    @click.self="close"
  >
    <div
      class="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-full overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="noticia-form-title"
    >
      <div class="flex items-center justify-between px-6 pt-5 pb-3">
        <h2 id="noticia-form-title" class="text-lg font-semibold text-gray-800">
          {{ isEdit ? 'Editar Noticia' : 'Nueva Noticia' }}
        </h2>
        <button type="button" class="text-gray-400 hover:text-gray-600 text-xl leading-none" aria-label="Cerrar" @click="close">
          &times;
        </button>
      </div>

      <p v-if="loading" class="px-6 pb-6 text-sm text-gray-500">Cargando noticia…</p>

      <form v-else class="px-6 pb-6 space-y-4" @submit.prevent="save">
        <p v-if="error" class="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{{ error }}</p>

        <div>
          <label class="block text-sm text-gray-600 mb-1" for="noticia-title">Título *</label>
          <input
            id="noticia-title"
            v-model="form.title"
            type="text"
            maxlength="200"
            placeholder="Título de la noticia"
            class="w-full border-gray-200 rounded-md text-sm focus:border-indigo-600 focus:ring-indigo-500"
          />
        </div>

        <div>
          <label class="block text-sm text-gray-600 mb-1" for="noticia-excerpt">Resumen</label>
          <textarea
            id="noticia-excerpt"
            v-model="form.excerpt"
            rows="3"
            placeholder="Resumen breve de la noticia"
            class="w-full border-gray-200 rounded-md text-sm focus:border-indigo-600 focus:ring-indigo-500"
          />
        </div>

        <div>
          <label class="block text-sm text-gray-600 mb-1">Contenido *</label>
          <RichTextEditor v-model="form.content" placeholder="Contenido completo de la noticia..." />
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label class="block text-sm text-gray-600 mb-1" for="noticia-status">Estado *</label>
            <select
              id="noticia-status"
              v-model="form.status"
              class="w-full border-gray-200 rounded-md text-sm focus:border-indigo-600 focus:ring-indigo-500"
            >
              <option v-for="s in statuses" :key="s.value" :value="s.value">{{ s.label }}</option>
            </select>
          </div>
          <div>
            <label class="block text-sm text-gray-600 mb-1" for="noticia-category">Categoría</label>
            <select
              id="noticia-category"
              v-model="form.category_id"
              class="w-full border-gray-200 rounded-md text-sm focus:border-indigo-600 focus:ring-indigo-500"
            >
              <option value="">Sin categoría</option>
              <option v-for="c in availableCategories" :key="c.id" :value="c.id">{{ c.name }}</option>
            </select>
          </div>
        </div>

        <div class="flex justify-end gap-2 pt-2">
          <button
            type="button"
            :disabled="saving"
            class="px-4 py-2 text-sm text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 disabled:opacity-50"
            @click="close"
          >
            Cancelar
          </button>
          <button
            type="submit"
            :disabled="saving"
            class="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {{ saving ? 'Guardando…' : isEdit ? 'Actualizar' : 'Crear' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import RichTextEditor from '@/components/commons/RichTextEditor.vue';
import { blogPostsApi } from '@/service';
import { BLOG_STATUSES, apiErrorMessage } from '@/utils/blog';

const stripHtml = (html) => (html || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();

export default {
  name: 'NoticiaFormModal',
  components: { RichTextEditor },
  props: {
    // Noticia a editar; null para crear una nueva.
    post: { type: Object, default: null },
    categories: { type: Array, default: () => [] },
  },
  emits: ['close', 'saved'],
  data() {
    return {
      statuses: BLOG_STATUSES,
      loading: false,
      saving: false,
      error: '',
      form: { title: '', excerpt: '', content: '', status: 'draft', category_id: '' },
    };
  },
  computed: {
    isEdit() {
      return !!this.post;
    },
    // Categorías activas, más la actual del post aunque esté inactiva (para no perderla al editar).
    availableCategories() {
      return this.categories.filter((c) => c.active || c.id === this.form.category_id);
    },
  },
  async created() {
    if (!this.isEdit) return;
    this.loading = true;
    try {
      // El detalle garantiza que `content` viene completo.
      const { data } = await blogPostsApi.get(this.post.id);
      const post = data.data || this.post;
      this.form = {
        title: post.title || '',
        excerpt: post.excerpt || '',
        content: post.content || '',
        status: post.status || 'draft',
        category_id: post.category_id || '',
      };
    } catch (e) {
      this.error = apiErrorMessage(e, 'No se pudo cargar la noticia.');
    } finally {
      this.loading = false;
    }
  },
  methods: {
    close() {
      if (!this.saving) this.$emit('close');
    },
    async save() {
      this.error = '';
      if (!this.form.title.trim()) {
        this.error = 'El título es obligatorio.';
        return;
      }
      if (!stripHtml(this.form.content)) {
        this.error = 'El contenido es obligatorio.';
        return;
      }
      // No se envían slug ni post_key: el backend genera el slug al crear y lo conserva al editar.
      const body = {
        title: this.form.title.trim(),
        excerpt: this.form.excerpt.trim(),
        content: this.form.content,
        status: this.form.status,
        category_id: this.form.category_id || null,
      };
      this.saving = true;
      try {
        if (this.isEdit) {
          await blogPostsApi.update(this.post.id, body);
        } else {
          await blogPostsApi.create(body);
        }
        this.$emit('saved');
      } catch (e) {
        this.error = apiErrorMessage(e, 'No se pudo guardar la noticia.');
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
